<template>
  <div class="modal-overlay" @click.self="onBackdropClick">
    <div class="modal-content">
      <button
        v-if="canClose"
        class="close-icon"
        @click="close"
        title="Fermer"
      >×</button>
      <h3 class="modal-title">Nouveau workspace</h3>
      <p class="modal-subtitle">
        Donne un nom à ton workspace et dépose tes documents (PDF, DOCX, images).
      </p>

      <label class="field-label" for="ws-name">Nom</label>
      <input
        id="ws-name"
        ref="nameInput"
        v-model="name"
        type="text"
        class="name-input"
        :class="{ 'name-input--missing': hasFiles && !name.trim() && phase === 'idle' }"
        :disabled="phase !== 'idle'"
        placeholder="Ex : Appel d'offres Métropole de Lyon"
        @keyup.enter="start"
      />
      <p v-if="hasFiles && !name.trim() && phase === 'idle'" class="name-missing-hint">
        Donne un nom à ton workspace pour activer le bouton « Intégrer ».
      </p>

      <div
        class="drop-zone"
        :class="{ active: dragging, disabled: phase !== 'idle' }"
        @dragover.prevent="dragging = phase === 'idle'"
        @dragleave.prevent="dragging = false"
        @drop.prevent="onDrop"
      >
        <p v-if="queue.length === 0">📄 Glissez des fichiers ou un dossier ici</p>
        <p v-else>{{ uploadable.length }} fichier(s) en file</p>
        <p class="drop-hint">PDF, DOCX, images : {{ maxFileLabel }} max par fichier</p>
        <div class="picker-buttons">
          <button
            type="button"
            class="picker-btn"
            :disabled="phase !== 'idle'"
            @click="$refs.fileInput.click()"
          >
            Fichiers
          </button>
          <button
            type="button"
            class="picker-btn"
            :disabled="phase !== 'idle'"
            @click="$refs.folderInput.click()"
          >
            Dossier
          </button>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept=".pdf,.docx,.png,.jpg,.jpeg,.webp,.heic,.heif"
          multiple
          style="display: none;"
          @change="onFilePick"
        />
        <input
          ref="folderInput"
          type="file"
          webkitdirectory
          directory
          multiple
          style="display: none;"
          @change="onFolderPick"
        />
      </div>

      <ul v-if="queue.length" class="file-list">
        <li
          v-for="(f, i) in queue"
          :key="i"
          :class="{ uploading: f.status === 'uploading', done: f.status === 'done', error: f.status === 'error' }"
        >
          <div class="file-main">
            <span class="file-name" :title="f.relativePath || f.file.name">
              <span v-if="f.relativePath && f.relativePath !== f.file.name" class="file-path">{{ dirname(f.relativePath) }}/</span>{{ f.file.name }}
            </span>
            <span class="file-size">{{ formatSize(f.file.size) }}</span>
            <span class="file-status">
              <span v-if="f.status === 'pending'">⏳</span>
              <span v-else-if="f.status === 'uploading'">⬆️</span>
              <span v-else-if="f.status === 'done'">✅</span>
              <span v-else-if="f.status === 'error'">❌</span>
            </span>
          </div>
          <div v-if="f.status === 'error' && f.error" class="file-error-msg">{{ f.error }}</div>
        </li>
      </ul>

      <div v-if="phase !== 'idle'" class="progress-block">
        <div class="progress-label">{{ phaseLabel }}</div>
        <div class="progress-bar"><div class="progress-fill" :style="{ width: progressPct + '%' }"></div></div>
      </div>

      <p v-if="errorMessage" class="error-msg">{{ errorMessage }}</p>

      <div v-if="phase === 'creating' || phase === 'uploading'" class="banner banner-danger">
        <strong>⚠️ Ne ferme pas cette fenêtre et ne quitte pas cette page.</strong><br>
        L'upload des fichiers est en cours. Fermer ou rafraîchir interromprait le transfert.
      </div>
      <div v-else-if="phase === 'ingesting'" class="banner banner-success">
        <strong>✓ Upload terminé.</strong>
        Tu peux fermer cette fenêtre — l'indexation continue en arrière-plan et apparaîtra dans la liste avec un indicateur de chargement.
      </div>

      <div class="actions">
        <button
          class="cancel-button"
          :disabled="!canClose"
          @click="close"
        >
          {{ phase === 'idle' ? 'Annuler' : 'Fermer' }}
        </button>
        <button
          v-if="phase === 'idle' || phase === 'creating' || phase === 'uploading'"
          class="confirm-button"
          :disabled="!canStart"
          :title="startBlockedReason"
          @click="start"
        >
          {{ startLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { getSupabaseToken } from '@/services/api';
import {
  MAX_UPLOAD_FILE_LABEL,
  MAX_DOCS_PER_WORKSPACE,
  isTooLarge,
  tooLargeMessage,
  uploadErrorMessage,
} from '@/utils/workspaceUpload';

const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';

export default {
  name: 'WorkspaceCreateModal',
  data() {
    return {
      name: '',
      queue: [],
      dragging: false,
      phase: 'idle', // idle → creating → uploading → ingesting (terminal côté modal)
      workspaceId: null,
      errorMessage: '',
    };
  },
  computed: {
    maxFileLabel() {
      return MAX_UPLOAD_FILE_LABEL;
    },
    // Fichiers réellement envoyables (hors fichiers refusés au dépôt, ex. trop gros).
    uploadable() {
      return this.queue.filter((f) => !f.rejected);
    },
    hasFiles() {
      return this.uploadable.length > 0;
    },
    canStart() {
      return this.phase === 'idle' && this.name.trim().length > 0 && this.hasFiles;
    },
    phaseLabel() {
      const map = {
        creating: 'Création du workspace…',
        uploading: `Upload des fichiers (${this.queue.filter((f) => f.status === 'done').length}/${this.uploadable.length})`,
        ingesting: 'Ingestion lancée en arrière-plan',
      };
      return map[this.phase] || '';
    },
    progressPct() {
      if (this.phase === 'creating') return 10;
      if (this.phase === 'uploading') {
        const done = this.queue.filter((f) => f.status === 'done').length;
        return 10 + Math.round((done / Math.max(this.uploadable.length, 1)) * 80);
      }
      if (this.phase === 'ingesting') return 100;
      return 0;
    },
    startLabel() {
      if (this.phase === 'idle') return 'Intégrer 🚀';
      return 'En cours…';
    },
    canClose() {
      // On ne peut fermer que si on est en idle (rien lancé) ou ingesting (upload fini)
      return this.phase === 'idle' || this.phase === 'ingesting';
    },
    startBlockedReason() {
      if (this.phase !== 'idle') return '';
      if (!this.name.trim() && !this.hasFiles) return 'Donne un nom et dépose au moins un fichier';
      if (!this.name.trim()) return 'Donne un nom à ton workspace';
      if (!this.hasFiles) return 'Dépose au moins un fichier';
      return '';
    },
  },
  mounted() {
    window.addEventListener('beforeunload', this.onBeforeUnload);
    window.addEventListener('keydown', this.onKeyDown);
    this.$nextTick(() => {
      if (this.$refs.nameInput) this.$refs.nameInput.focus();
    });
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
  },
  methods: {
    onFilePick(e) {
      const files = Array.from(e.target.files || []).map((file) => ({ file, relativePath: file.name }));
      this.addEntries(files);
      e.target.value = '';
    },
    onFolderPick(e) {
      const files = Array.from(e.target.files || []).map((file) => ({
        file,
        relativePath: file.webkitRelativePath || file.name,
      }));
      this.addEntries(files);
      e.target.value = '';
    },
    async onDrop(e) {
      this.dragging = false;
      if (this.phase !== 'idle') return;
      const items = e.dataTransfer.items;
      if (items && items.length && items[0].webkitGetAsEntry) {
        const entries = [];
        for (let i = 0; i < items.length; i += 1) {
          const entry = items[i].webkitGetAsEntry && items[i].webkitGetAsEntry();
          if (entry) entries.push(entry);
        }
        const collected = [];
        await Promise.all(entries.map((entry) => this.walkEntry(entry, '', collected)));
        this.addEntries(collected);
      } else {
        const files = Array.from(e.dataTransfer.files || []).map((file) => ({ file, relativePath: file.name }));
        this.addEntries(files);
      }
    },
    walkEntry(entry, prefix, out) {
      return new Promise((resolve) => {
        if (entry.isFile) {
          entry.file((file) => {
            out.push({ file, relativePath: prefix ? `${prefix}/${file.name}` : file.name });
            resolve();
          }, () => resolve());
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          const collected = [];
          const readBatch = () => {
            reader.readEntries((batch) => {
              if (!batch.length) {
                Promise.all(collected.map((ee) => this.walkEntry(ee, prefix ? `${prefix}/${entry.name}` : entry.name, out))).then(resolve);
                return;
              }
              collected.push(...batch);
              readBatch();
            }, () => resolve());
          };
          readBatch();
        } else {
          resolve();
        }
      });
    },
    addEntries(entries) {
      const allowedExt = ['.pdf', '.docx', '.png', '.jpg', '.jpeg', '.webp', '.heic', '.heif'];

      // Filtre extension
      const extOk = [];
      let skippedExt = 0;
      entries.forEach((e) => {
        const n = (e.file.name || '').toLowerCase();
        if (allowedExt.some((ext) => n.endsWith(ext))) extOk.push(e);
        else skippedExt += 1;
      });
      if (skippedExt > 0 && this.$toast) {
        this.$toast.info(`${skippedExt} fichier(s) ignoré(s) (format non supporté)`);
      }

      // Filtre taille : les fichiers trop gros restent visibles dans la file,
      // en erreur, avec la raison écrite en clair.
      const tooLarge = extOk.filter((e) => isTooLarge(e.file));
      const sizeOk = extOk.filter((e) => !isTooLarge(e.file));
      if (tooLarge.length > 0 && this.$toast) {
        this.$toast.error(`${tooLarge.length} fichier(s) trop volumineux : ${MAX_UPLOAD_FILE_LABEL} maximum par fichier.`);
      }

      // Cap nombre total de fichiers
      const remaining = Math.max(0, MAX_DOCS_PER_WORKSPACE - this.uploadable.length);
      const accepted = sizeOk.slice(0, remaining);
      const skippedCount = sizeOk.length - accepted.length;
      if (skippedCount > 0 && this.$toast) {
        this.$toast.error(`${skippedCount} fichier(s) ignoré(s) : limite de ${MAX_DOCS_PER_WORKSPACE} fichiers par workspace atteinte`);
      }

      this.queue.push(...tooLarge.map(({ file, relativePath }) => ({
        file,
        relativePath,
        status: 'error',
        error: tooLargeMessage(file),
        rejected: true,
      })));
      this.queue.push(...accepted.map(({ file, relativePath }) => ({
        file,
        relativePath,
        status: 'pending',
        error: null,
      })));
    },
    dirname(path) {
      const idx = path.lastIndexOf('/');
      return idx > 0 ? path.slice(0, idx) : '';
    },
    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1024 / 1024).toFixed(1) + ' MB';
    },
    async start() {
      if (!this.canStart) return;
      this.errorMessage = '';
      const token = await getSupabaseToken();

      // 1. Create workspace
      this.phase = 'creating';
      try {
        const { data } = await axios.post(
          `${API_URL}/copilot/api/v1/workspace`,
          { name: this.name.trim() },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        this.workspaceId = data.workspace_id;
        // Annonce immédiate au dashboard : il peut déjà afficher le workspace avec un loader
        this.$emit('workspace-created', {
          id: data.workspace_id,
          name: this.name.trim(),
          status: 'draft',
        });
      } catch (e) {
        console.error('create workspace failed', e);
        this.errorMessage = 'Échec de la création du workspace.';
        this.phase = 'idle';
        return;
      }

      // 2. Upload files sequentially (fire-and-forget côté UX : le parent peut close)
      this.phase = 'uploading';
      for (const entry of this.queue) {
        if (entry.status !== 'pending') continue;
        entry.status = 'uploading';
        let resp = null;
        try {
          const form = new FormData();
          form.append('file', entry.file);
          if (entry.relativePath && entry.relativePath !== entry.file.name) {
            form.append('relative_path', entry.relativePath);
          }
          resp = await fetch(`${API_URL}/copilot/api/v1/workspace/${this.workspaceId}/upload`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: form,
          });
          if (resp.ok) {
            entry.status = 'done';
            continue;
          }
          entry.status = 'error';
          entry.error = await uploadErrorMessage({ resp, file: entry.file });
        } catch (e) {
          entry.status = 'error';
          entry.error = await uploadErrorMessage({ resp, error: e, file: entry.file });
        }
        console.error('upload failed', entry.file.name, resp && resp.status, entry.error);
      }

      const uploadedCount = this.queue.filter((f) => f.status === 'done').length;
      if (uploadedCount === 0) {
        this.errorMessage = 'Aucun fichier n\'a pu être envoyé : voir le détail sous chaque fichier.';
        this.phase = 'idle';
        return;
      }

      // 3. Launch ingest (fire-and-forget côté backend) et on bascule en ingesting
      this.phase = 'ingesting';
      try {
        await axios.post(
          `${API_URL}/copilot/api/v1/workspace/${this.workspaceId}/ingest`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (e) {
        console.error('ingest launch failed', e);
        this.errorMessage = 'Échec du lancement de l\'ingestion (tu peux réessayer depuis la liste).';
        // On garde phase='ingesting' pour laisser l'UX cohérente; le dashboard verra le failed via polling
      }

      // 4. Tout est lancé côté serveur — on notifie le dashboard, l'utilisateur ferme quand il veut.
      this.$emit('ingest-started', { id: this.workspaceId, pdfCount: uploadedCount });
    },
    close() {
      if (!this.canClose) return;
      this.$emit('close');
    },
    onBackdropClick() {
      if (!this.canClose) return;
      this.$emit('close');
    },
    onKeyDown(e) {
      if (e.key === 'Escape' && this.canClose) this.$emit('close');
    },
    onBeforeUnload(e) {
      if (this.phase === 'creating' || this.phase === 'uploading') {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
      return undefined;
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex; justify-content: center; align-items: center;
  z-index: 1000;
}

.modal-content {
  position: relative;
  background: #fff;
  padding: 28px;
  border-radius: 10px;
  width: 560px;
  max-width: 95%;
  max-height: 88vh;
  overflow-y: auto;
}

.close-icon {
  position: absolute;
  top: 10px;
  right: 14px;
  background: transparent;
  border: none;
  font-size: 1.8rem;
  line-height: 1;
  color: #9ca3af;
  cursor: pointer;
  padding: 2px 6px;
}
.close-icon:hover { color: #374151; }

.modal-title {
  font-weight: bold;
  margin: 0 0 8px 0;
  color: #111827;
  font-size: 1.3rem;
}

.modal-subtitle {
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 18px;
}

.field-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.name-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.95rem;
  margin-bottom: 18px;
  box-sizing: border-box;
}
.name-input:focus { border-color: #6c63ff; outline: none; box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.15); }
.name-input:disabled { background: #f9fafb; }
.name-input--missing { border-color: #f59e0b; background: #fffbeb; }
.name-input--missing:focus { border-color: #d97706; box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15); }

.name-missing-hint {
  margin-top: -12px;
  margin-bottom: 14px;
  font-size: 0.8rem;
  color: #b45309;
  display: flex;
  align-items: center;
  gap: 6px;
}
.name-missing-hint::before {
  content: '⚠';
  font-size: 0.9rem;
}

.drop-zone {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 28px 16px;
  text-align: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
  margin-bottom: 16px;
}
.drop-zone.active { border-color: #6c63ff; background: #f5f3ff; color: #6c63ff; }
.drop-zone.disabled { opacity: 0.55; pointer-events: none; }

.picker-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 12px;
}
.picker-btn {
  background: #fff;
  color: #6c63ff;
  border: 1px solid #6c63ff;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
}
.picker-btn:hover:not(:disabled) { background: #f5f3ff; }
.picker-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.file-path { color: #9ca3af; font-size: 0.8rem; margin-right: 2px; }

.file-list {
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
  max-height: 180px;
  overflow-y: auto;
}
.file-list li {
  padding: 7px 10px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.875rem;
}
.file-list li.done { background: #ecfdf5; }
.file-list li.error { background: #fef2f2; }
.file-main { display: flex; align-items: center; gap: 10px; }
.file-error-msg { margin-top: 3px; color: #b91c1c; font-size: 0.78rem; line-height: 1.35; }
.drop-hint { margin: 4px 0 0; color: #6b7280; font-size: 0.78rem; }

.file-name { flex: 1; color: #1f2937; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.file-size { color: #6b7280; font-size: 0.8rem; }

.progress-block {
  margin-bottom: 14px;
}
.progress-label {
  font-size: 0.85rem;
  color: #374151;
  margin-bottom: 6px;
}
.progress-bar {
  height: 8px;
  background: #e0e4e8;
  border-radius: 4px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6c63ff, #5a52d5);
  transition: width 0.25s;
}

.error-msg {
  color: #b91c1c;
  font-size: 0.85rem;
  margin: 6px 0 12px 0;
}
.info-msg {
  color: #4b5563;
  font-size: 0.8rem;
  margin: 4px 0 12px 0;
  font-style: italic;
}

.banner {
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  margin: 8px 0 14px 0;
}
.banner-danger {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}
.banner-success {
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.cancel-button, .confirm-button {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
}
.cancel-button { background: #e0e4e8; color: #374151; }
.cancel-button:hover:not(:disabled) { background: #d1d5db; }
.confirm-button {
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  color: white;
  box-shadow: 0 2px 6px rgba(108, 99, 255, 0.25);
  transition: filter 0.15s, box-shadow 0.15s;
}
.confirm-button:hover:not(:disabled) { filter: brightness(1.06); box-shadow: 0 3px 10px rgba(108, 99, 255, 0.35); }
.cancel-button:disabled, .confirm-button:disabled { opacity: 0.55; cursor: not-allowed; }
</style>
